import { NotificationData } from './ExpoNotificationService';
import store from '../store';
import { updateSelectedCharger } from '../store/actions/charging-actions';
import { Platform } from 'react-native';
import { liveActivityService } from './LiveActivityService';
import { androidChargingNotificationService } from './AndroidChargingNotificationService';
import { registerLiveActivityToken, unregisterLiveActivityToken } from './charger-service';

/**
 * Central handler for mapping notification payloads to navigation routes
 */
export class NotificationNavigationHandler {
  /**
   * Screens that require pre-loading charger data before navigation
   */
  private static readonly CHARGER_SCREENS = ['ChargerStation', 'ChargingScreen'];

  /**
   * Notification types that should trigger Live Activity start (iOS Dynamic Island)
   */
  private static readonly LIVE_ACTIVITY_TRIGGER_TYPES = [
    'reservation_reminder',  // 15 min before scheduled charge
    'charge_started',        // Charge has begun
    'charge_status',         // Status update that charge is active
    'scheduled_charge_starting', // New: explicit type for scheduled charge start
  ];

  /**
   * Notification types that should end Live Activity
   */
  private static readonly LIVE_ACTIVITY_END_TYPES = [
    'charge_completed',
    'charging_complete',
    'charge_cancelled',
  ];

  /**
   * Map notification data to navigation parameters
   */
  static getNavigationFromPayload(data: NotificationData): { 
    screen: string; 
    params?: any;
  } | null {
    if (!data) return null;

    console.log('Mapping notification payload to navigation:', data);

    // PRIORITY 1: Handle notifications with explicit screen field
    // Format: { screen: "ChargerStation", chargerUuid: "abc123", type: "charger_fault", ... }
    if (data.screen && this.isValidScreen(data.screen)) {
      console.log('Processing notification with explicit screen:', data.screen);
      
      // Build params from the notification data (excluding metadata fields)
      const params: any = {};
      
      // Extract chargerUuid if present
      if (data.chargerUuid) {
        params.chargerUuid = data.chargerUuid;
      }
      
      // Extract other useful fields
      if (data.type) params.notificationType = data.type;
      if (data.errorCode) params.errorCode = data.errorCode;
      if (data.newStatus) params.newStatus = data.newStatus;
      
      // Include any explicit params object
      if (data.params) {
        Object.assign(params, this.sanitizeParams(data.params));
      }
      
      return {
        screen: data.screen,
        params: this.sanitizeParams(params)
      };
    }

    // PRIORITY 2: Handle old backend format: { type: "navigate", screen: "...", params: {...} }
    if (data.type === 'navigate' && data.screen) {
      console.log('Processing navigate type notification:', { screen: data.screen, params: data.params });
      return {
        screen: data.screen,
        params: this.sanitizeParams(data.params || {})
      };
    }

    // PRIORITY 3: Handle legacy notification types for backward compatibility
    switch (data.type) {
      // Charger fault/error notifications (Host)
      case 'charger_fault':
      case 'charger_issue':
        if (data.chargerUuid || data.id) {
          return {
            screen: 'ChargerStation',
            params: { 
              chargerUuid: data.chargerUuid || data.id, 
              showIssue: true,
              errorCode: data.errorCode 
            }
          };
        }
        break;

      // Charger recovered (Host)
      case 'charger_recovered':
        if (data.chargerUuid || data.id) {
          return {
            screen: 'ChargerStation',
            params: { 
              chargerUuid: data.chargerUuid || data.id,
              newStatus: data.newStatus 
            }
          };
        }
        break;

      // Charging interrupted (Driver)
      case 'charging_interrupted':
        if (data.chargerUuid || data.id) {
          return {
            screen: 'ChargerStation',
            params: { 
              chargerUuid: data.chargerUuid || data.id,
              errorCode: data.errorCode,
              showIssue: true
            }
          };
        }
        break;

      // Charging resumed (Driver)
      case 'charging_resumed':
        if (data.chargerUuid || data.id) {
          return {
            screen: 'ChargerStation',
            params: { 
              chargerUuid: data.chargerUuid || data.id,
              newStatus: data.newStatus
            }
          };
        }
        break;

      // Charge started
      case 'charge_started':
      case 'charge_status':
        if (data.chargerUuid || data.id) {
          return {
            screen: 'ChargingScreen',
            params: { chargerUuid: data.chargerUuid, chargeId: data.id }
          };
        }
        break;

      // Charge completed
      case 'charge_completed':
      case 'charging_complete':
        if (data.id || data.chargerUuid) {
          return {
            screen: 'ChargeSummary',
            params: { chargeId: data.id, chargerUuid: data.chargerUuid, showSummary: true }
          };
        }
        break;

      // Payment notifications
      case 'payment_received':
        if (data.chargerUuid || data.id) {
          return {
            screen: 'ChargerStation',
            params: { chargerUuid: data.chargerUuid || data.id }
          };
        }
        break;

      case 'payment_failed':
      case 'payment_required':
        if (data.id) {
          return {
            screen: 'SetupBilling',
            params: { chargeId: data.id }
          };
        }
        break;

      // Charger available
      case 'charger_available':
        if (data.chargerUuid || data.id) {
          return {
            screen: 'ChargerStation', 
            params: { chargerUuid: data.chargerUuid || data.id }
          };
        }
        break;

      // Reservation reminder
      case 'reservation_reminder':
        if (data.chargerUuid || data.id) {
          return {
            screen: 'ChargerStation',
            params: { chargerUuid: data.chargerUuid || data.id }
          };
        }
        break;

      case 'account_notification':
        return {
          screen: 'AccountInfo',
          params: {}
        };

      case 'promotional':
        // Handle promotional notifications
        if (data.deepLink) {
          return this.parseDeepLink(data.deepLink, data.params);
        }
        break;

      default:
        // Handle custom deep links
        if (data.deepLink) {
          return this.parseDeepLink(data.deepLink, data.params);
        }
        break;
    }

    // Default fallback
    return {
      screen: 'WelcomeScreen',
      params: {}
    };
  }

  /**
   * Parse deep link format: "screen/param1/value1/param2/value2"
   */
  private static parseDeepLink(deepLink: string, additionalParams?: any): {
    screen: string;
    params?: any;
  } | null {
    try {
      const parts = deepLink.split('/').filter(Boolean);
      if (parts.length === 0) return null;

      const screen = parts[0];
      const params: any = { ...additionalParams };

      // Parse parameters from deep link
      for (let i = 1; i < parts.length; i += 2) {
        if (i + 1 < parts.length) {
          params[parts[i]] = parts[i + 1];
        }
      }

      return { screen, params };
    } catch (error) {
      console.error('Error parsing deep link:', deepLink, error);
      return null;
    }
  }

  /**
   * Start Live Activity (iOS) or Ongoing Notification (Android) from notification data
   * Called when receiving charging-related notifications
   */
  static async startLiveActivityFromNotification(data: NotificationData): Promise<boolean> {
    // Check if this notification type should trigger a Live Activity / Notification
    if (!data.type || !this.LIVE_ACTIVITY_TRIGGER_TYPES.includes(data.type)) {
      return false;
    }

    const orderId = data.orderId || data.id;
    if (!orderId) {
      console.log('[NotificationNavigationHandler] No orderId in notification, cannot start charging UI');
      return false;
    }

    // Get required data from notification
    const chargerName = data.chargerName || data.name || 'Charger';
    const chargerAddress = data.chargerAddress || data.address || '';
    const chargingCapacity = data.chargingCapacity || 40;
    const pricePerKwh = data.pricePerKwh || 0.15;

    // ========== iOS: Live Activity (Dynamic Island) ==========
    if (Platform.OS === 'ios') {
      // Check if there's already an active Live Activity for this order
      const currentInfo = await liveActivityService.getCurrentActivityInfo();
      
      if (currentInfo?.isActive && currentInfo?.orderId === orderId) {
        console.log('[NotificationNavigationHandler] Live Activity already active for this order');
        return true;
      }

      try {
        console.log('[NotificationNavigationHandler] Starting Live Activity from notification:', {
          type: data.type,
          orderId,
          chargerName,
        });

        const result = await liveActivityService.startActivity({
          chargerName,
          chargerAddress,
          orderId,
          chargingCapacity,
          pricePerKwh,
        });

        if (result?.started) {
          console.log('[NotificationNavigationHandler] Live Activity started successfully from notification');
          
          // Register push token with backend for server-side updates
          if (result.pushToken) {
            try {
              const regResponse = await registerLiveActivityToken(result.pushToken, orderId);
              if (regResponse.payload?.success) {
                console.log('[NotificationNavigationHandler] Live Activity token registered with backend');
              } else {
                console.warn('[NotificationNavigationHandler] Failed to register token:', regResponse.payload?.message);
              }
            } catch (regError) {
              console.error('[NotificationNavigationHandler] Error registering Live Activity token:', regError);
            }
          }
          
          return true;
        }
      } catch (error) {
        console.error('[NotificationNavigationHandler] Error starting Live Activity:', error);
      }
    }

    // ========== Android: Ongoing Notification ==========
    if (Platform.OS === 'android') {
      // Check if there's already an active notification for this order
      if (androidChargingNotificationService.isNotificationForOrder(orderId)) {
        console.log('[NotificationNavigationHandler] Android notification already active for this order');
        return true;
      }

      try {
        console.log('[NotificationNavigationHandler] Starting Android notification from push:', {
          type: data.type,
          orderId,
          chargerName,
        });

        const started = await androidChargingNotificationService.startNotification({
          chargerName,
          orderId,
        });

        if (started) {
          console.log('[NotificationNavigationHandler] Android notification started from push');
          return true;
        }
      } catch (error) {
        console.error('[NotificationNavigationHandler] Error starting Android notification:', error);
      }
    }

    return false;
  }

  /**
   * Pre-load charger data into Redux before navigating to charger-related screens
   */
  private static async preloadChargerData(chargerUuid: string): Promise<boolean> {
    try {
      console.log('Pre-loading charger data for UUID:', chargerUuid);
      
      // Dispatch the action to fetch and set the selected charger
      await store.dispatch(updateSelectedCharger(chargerUuid) as any);
      
      // Verify the charger was loaded
      const state = store.getState();
      const loadedCharger = state.chargers?.selectedCharger;
      
      if (loadedCharger?.uuid === chargerUuid) {
        console.log('Charger data pre-loaded successfully:', loadedCharger.name || chargerUuid);
        return true;
      } else {
        console.warn('Charger data may not have loaded correctly');
        return false;
      }
    } catch (error) {
      console.error('Error pre-loading charger data:', error);
      return false;
    }
  }

  /**
   * Execute navigation using RootNavigation
   * This function is designed to be crash-safe and will not throw
   */
  static async navigateFromNotification(data: NotificationData): Promise<void> {
    // Guard against null/undefined data
    if (!data) {
      console.warn('[NotificationNavigationHandler] navigateFromNotification called with null data');
      return;
    }

    const navigationInfo = this.getNavigationFromPayload(data);
    
    if (!navigationInfo) {
      console.log('No navigation info found for notification data:', data);
      return;
    }

    try {
      // Import navigation here to avoid circular dependencies
      const RootNavigation = require('../../RootNavigation');
      
      console.log('Navigating to:', navigationInfo.screen, navigationInfo.params);

      // ============================================================
      // LIVE ACTIVITY: Start Dynamic Island widget for charging notifications
      // This runs before navigation so the widget appears immediately
      // Wrapped in try/catch to prevent crashes
      // ============================================================
      if (Platform.OS === 'ios') {
        try {
          await this.startLiveActivityFromNotification(data);
        } catch (liveActivityError) {
          console.warn('[NotificationNavigationHandler] Error starting Live Activity during navigation:', liveActivityError);
        }
      }

      // Check if this is a charger-related screen that needs pre-loading
      const needsChargerPreload = this.CHARGER_SCREENS.includes(navigationInfo.screen);
      const chargerUuid = navigationInfo.params?.chargerUuid || navigationInfo.params?.chargerId;

      if (needsChargerPreload && chargerUuid) {
        console.log('Pre-loading charger data before navigation...');
        try {
          const preloadSuccess = await this.preloadChargerData(chargerUuid);
          if (!preloadSuccess) {
            console.warn('Charger pre-load failed, navigating anyway');
          }
        } catch (preloadError) {
          console.warn('Charger pre-load error, navigating anyway:', preloadError);
        }
      }
      
      // Add a small delay to ensure navigation is ready
      setTimeout(() => {
        try {
          RootNavigation.navigate(navigationInfo.screen, navigationInfo.params);
        } catch (navError) {
          console.error('Navigation execute error:', navError);
          // Try fallback
          try {
            RootNavigation.navigate('WelcomeScreen');
          } catch {}
        }
      }, 100);

    } catch (error) {
      console.error('Navigation error:', error);
      
      // Fallback to home screen
      try {
        const RootNavigation = require('../../RootNavigation');
        setTimeout(() => {
          try {
            RootNavigation.navigate('WelcomeScreen');
          } catch {}
        }, 100);
      } catch (fallbackError) {
        console.error('Fallback navigation also failed:', fallbackError);
      }
    }
  }

  /**
   * Handle notification received in foreground (without navigation)
   * Use this when you want to start/end Live Activity without navigating
   * This function is designed to be crash-safe and will not throw
   */
  static async handleForegroundNotification(data: NotificationData): Promise<void> {
    // Guard against null/undefined data
    if (!data) {
      console.warn('[NotificationNavigationHandler] handleForegroundNotification called with null data');
      return;
    }

    console.log('[NotificationNavigationHandler] Handling foreground notification:', data.type);
    
    if (Platform.OS !== 'ios') {
      return;
    }
    
    try {
      // Check if this notification should end a Live Activity
      if (data.type && this.LIVE_ACTIVITY_END_TYPES.includes(data.type)) {
        await this.endLiveActivityFromNotification(data);
        return;
      }
      
      // Start Live Activity if applicable
      await this.startLiveActivityFromNotification(data);
    } catch (error) {
      // Catch-all to ensure this function never crashes the app
      console.error('[NotificationNavigationHandler] Error handling foreground notification:', error);
    }
  }

  /**
   * End Live Activity from notification data
   * Called when receiving charge completion notifications
   * This function is designed to be crash-safe and will not throw
   */
  static async endLiveActivityFromNotification(data: NotificationData): Promise<boolean> {
    if (Platform.OS !== 'ios') {
      return false;
    }

    // Check if this notification type should end a Live Activity
    if (!data?.type || !this.LIVE_ACTIVITY_END_TYPES.includes(data.type)) {
      return false;
    }

    try {
      // Check if there's an active Live Activity for this order
      const orderId = data.orderId || data.id;
      let currentInfo;
      
      try {
        currentInfo = await liveActivityService.getCurrentActivityInfo();
      } catch (infoError) {
        console.warn('[NotificationNavigationHandler] Error getting activity info:', infoError);
        // Try to end all activities as fallback
        try {
          await liveActivityService.endAllActivities();
        } catch {}
        return false;
      }
      
      if (!currentInfo?.isActive) {
        console.log('[NotificationNavigationHandler] No active Live Activity to end');
        return false;
      }

      // If orderId is provided, only end if it matches
      if (orderId && currentInfo.orderId !== orderId) {
        console.log('[NotificationNavigationHandler] Live Activity is for different order');
        return false;
      }

      // Get final stats from notification if available
      // Use safe defaults to prevent NaN errors
      const finalKwh = Number(data.totalKwh || data.kwhCharged) || 0;
      const finalCost = Number(data.totalCost || data.estimatedCost) || 0;
      const totalTimeSeconds = Number(data.totalTimeSeconds || data.timeElapsedSeconds) || 0;

      console.log('[NotificationNavigationHandler] Ending Live Activity from notification:', {
        type: data.type,
        orderId,
        finalKwh,
        finalCost,
      });

      try {
        await liveActivityService.endActivity(finalKwh, finalCost, totalTimeSeconds);
      } catch (endError) {
        console.warn('[NotificationNavigationHandler] Error ending Live Activity:', endError);
        // Try to force end all activities as fallback
        try {
          await liveActivityService.endAllActivities();
        } catch {}
      }
      
      // Unregister token from backend - fire and forget
      if (orderId) {
        unregisterLiveActivityToken(orderId).catch((unregError) => {
          console.warn('[NotificationNavigationHandler] Error unregistering token:', unregError);
        });
      }
      
      return true;
    } catch (error) {
      // Catch-all to ensure this function never throws
      console.error('[NotificationNavigationHandler] Unexpected error ending Live Activity:', error);
      // Last resort: try to clean up all activities
      try {
        await liveActivityService.endAllActivities();
      } catch {}
      return false;
    }
  }

  /**
   * Check if a screen exists in the navigation stack
   * Based on RootStackParamList from navigation/stackTypes.ts
   */
  static isValidScreen(screenName: string): boolean {
    const validScreens = [
      // Auth & Onboarding
      'LoginScreen',
      'LoginWithAccount',
      'CreateAccount',
      'WelcomeScreen',
      'OnboardingStatus',
      'resetPassword',
      
      // Charger & Charging
      'ChargerStation',
      'ChargerScan',
      'ChargerSelection',
      'ChargerValidation',
      'ChargingScreen',
      'ChargeSummary',
      'ChargingStationNotFound',
      'SearchCharger',
      
      // Charger Management
      'MyChargers',
      'ManageChargers',
      'RegisterUpdateCharger',
      'ChargerQRCode',
      'ChargerEventLogs',
      
      // Account & Settings
      'AccountInfo',
      'TransactionHistory',
      'SetupBilling',
      'SetupBillingAddress',
      'PayoutRules',
      
      // Partner & Insights
      'PartnerPortal',
      'PartnerInsights',
      'HostInsights',
      'RevenueSplitDashboard',
      'PartnerEarningsDashboard',
      'SplitPartnerships',
      
      // Misc
      'ActiveChargeCard',
      'DatePicker',
      'AdminScreen',
      'TestErrorNotifications',
    ];

    return validScreens.includes(screenName);
  }

  /**
   * Sanitize navigation parameters to prevent injection
   */
  static sanitizeParams(params: any): any {
    if (!params || typeof params !== 'object') return {};

    const sanitized: any = {};
    
    for (const [key, value] of Object.entries(params)) {
      // Only allow basic types and reasonable key names
      if (typeof key === 'string' && 
          key.match(/^[a-zA-Z][a-zA-Z0-9_]*$/) && 
          key.length < 50) {
        
        if (typeof value === 'string' || 
            typeof value === 'number' || 
            typeof value === 'boolean') {
          sanitized[key] = value;
        }
      }
    }

    return sanitized;
  }
}

export default NotificationNavigationHandler;
